import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: (
      typeof window !== 'undefined' && window.env
        ? `${window.env.api_base_url}/v1/`
        : (`${process.env.API_BASE_URL}/v1/` || 'http://localhost:3000/v1/')
    ),
    credentials: 'include',
    prepareHeaders(headers, { extra: cookies }) {
      if (cookies) {
        headers.set('Cookie', cookies);
      }

      return headers;
    },
  }),
  tagTypes: ['Notifications', 'User', 'Preload', 'Subscription', 'GuidelineCompletions'],
  endpoints: (builder) => ({
    getCurrentUser: builder.query({
      query: () => 'sessions',
      transformResponse(baseQueryReturnValue) {
        const { meta, data } = baseQueryReturnValue;

        if (data) {
          return {
            ...data[0],
            _meta: meta,
          };
        }

        return null;
      },
      providesTags: ['User'],
    }),
    getNotifications: builder.query({
      query: () => 'notifications',
      transformResponse(baseQueryReturnValue) {
        return [
          // TODO: Remove this
          // { flag: 'SUBSCRIPTION', sub_flag: 'TRIAL_EXPIRED', read: false },
          ...baseQueryReturnValue.data,
        ];
      },
      providesTags: (result) => (result
        ? [...result.map(({ id }) => ({ type: 'Notifications', id })), 'Notifications', 'User']
        : ['Notifications', 'User']),
    }),
    getStripeBillingPortal: builder.query({
      query: (returnUrl) => ({
        url: 'sessions/billing_link',
        params: returnUrl ? { return_url: returnUrl } : {},
      }),
      transformResponse(baseQueryReturnValue) {
        return baseQueryReturnValue.data;
      },
    }),
    getPreload: builder.query({
      query: () => 'ssr_preload',
      providesTags: ['Preload'],
    }),
    createSubscription: builder.mutation({
      query: ({
        subscriptionPlan, tag, quantity, couponCode, currency, payment_method_id,
      }) => ({
        url: 'subscriptions',
        method: 'POST',
        body: {
          subscriptionPlan, tag, quantity, couponCode, currency, payment_method_id,
        },
      }),
      transformResponse(baseQueryReturnValue) {
        // Clean meta to remove non-serializable values like Request objects
        // RTK Query's meta can contain Request/Response objects which are not serializable
        // We only keep serializable metadata from the API response
        const cleanMeta = baseQueryReturnValue?.meta || {};

        if (baseQueryReturnValue.data && Array.isArray(baseQueryReturnValue.data)) {
          return {
            ...baseQueryReturnValue.data[0],
            _meta: cleanMeta,
          };
        }

        // If data is not an array, return the response with cleaned meta
        const responseData = baseQueryReturnValue?.data || baseQueryReturnValue;
        return {
          ...(typeof responseData === 'object' && responseData !== null ? responseData : {}),
          _meta: cleanMeta,
        };
      },
      invalidatesTags: ['User', 'Subscription'],
    }),
    resendVerificationEmail: builder.mutation({
      query: () => ({
        url: 'users/resend_verification_email',
        method: 'GET',
      }),
      transformResponse(baseQueryReturnValue) {
        // Return the response as-is, similar to Angular's callback pattern
        // Angular receives result directly, not wrapped
        return baseQueryReturnValue;
      },
      invalidatesTags: ['User'],
    }),
    getGuidelineCompletions: builder.query({
      query: () => 'guideline_completions',
      transformResponse(baseQueryReturnValue) {
        return baseQueryReturnValue.data || [];
      },
      providesTags: ['GuidelineCompletions'],
    }),
    completeGuidelineStep: builder.mutation({
      query: (stepKey) => ({
        url: 'guideline_completions',
        method: 'POST',
        body: { step_key: stepKey },
      }),
      transformResponse(baseQueryReturnValue) {
        return baseQueryReturnValue.data?.[0] || baseQueryReturnValue.data;
      },
      invalidatesTags: ['GuidelineCompletions', 'User'],
    }),
    createViewAs: builder.mutation({
      query: (userId) => ({
        url: 'view_as',
        method: 'POST',
        body: { user_id: userId },
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetCurrentUserQuery,
  useGetNotificationsQuery,
  useGetStripeBillingPortalQuery,
  useGetPreloadQuery,
  useCreateSubscriptionMutation,
  useResendVerificationEmailMutation,
  useGetGuidelineCompletionsQuery,
  useCompleteGuidelineStepMutation,
  useCreateViewAsMutation,
} = api;
